/**
 * Les documents de conformité d'un partenaire (docs/CADRAGE.md).
 *
 * L'attestation de vigilance URSSAF se renouvelle tous les six mois ; elle est
 * obligatoire au-delà de 5 000 € de contrat, c'est-à-dire pour tout chantier.
 */
export const TYPES_CONFORMITE = [
  { id: 'kbis', libelle: 'Kbis', obligatoire: true, validiteMois: 3 },
  { id: 'decennale', libelle: 'Assurance décennale', obligatoire: true, validiteMois: 12 },
  { id: 'urssaf', libelle: 'Attestation de vigilance URSSAF', obligatoire: true, validiteMois: 6 },
  {
    id: 'qualification',
    libelle: 'Qualification (RGE, Qualibat…)',
    obligatoire: false,
    validiteMois: 12,
  },
  { id: 'autre', libelle: 'Autre document', obligatoire: false, validiteMois: null },
] as const;

export type TypeConformite = (typeof TYPES_CONFORMITE)[number]['id'];

export function libelleConformite(type: TypeConformite): string {
  return TYPES_CONFORMITE.find((t) => t.id === type)?.libelle ?? type;
}

/** Nombre de jours avant l'échéance où l'on prévient. */
export const PREAVIS_JOURS = 30;

export type StatutConformite = 'a-jour' | 'bientot' | 'expire' | 'manquant';

export interface EtatConformite {
  statut: StatutConformite;
  /** Jours restants (négatif si expiré) ; absent pour un document sans échéance ou manquant. */
  jours?: number;
}

/** Jours entre deux dates calendaires « AAAA-MM-JJ », sans que l'heure d'été s'en mêle. */
export function joursEntre(du: string, au: string): number {
  const utc = (d: string) => {
    const [a, m, j] = d.split('-').map(Number);
    return Date.UTC(a ?? 0, (m ?? 1) - 1, j ?? 1);
  };
  return Math.round((utc(au) - utc(du)) / 86_400_000);
}

/** Aujourd'hui à Paris, au format « AAAA-MM-JJ ». */
export function aujourdhui(maintenant: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(maintenant);
}

export function etatDocument(expireLe: string | null, jour: string): EtatConformite {
  if (expireLe === null) return { statut: 'a-jour' };
  const jours = joursEntre(jour, expireLe);
  if (jours < 0) return { statut: 'expire', jours };
  if (jours <= PREAVIS_JOURS) return { statut: 'bientot', jours };
  return { statut: 'a-jour', jours };
}

const GRAVITE: Record<StatutConformite, number> = {
  'a-jour': 0,
  bientot: 1,
  manquant: 2,
  expire: 3,
};

export interface LigneConformite {
  type: TypeConformite;
  obligatoire: boolean;
  /** Le document le plus récent de ce type, s'il existe. */
  document: { id: string; expireLe: string | null } | null;
  etat: EtatConformite;
}

/**
 * Une ligne par type de document : le plus récent de chaque type compte, un
 * type obligatoire sans document est « manquant ».
 */
export function tableauConformite(
  documents: readonly {
    id: string;
    type: TypeConformite;
    expireLe: string | null;
    ajouteLe: string;
  }[],
  jour: string
): LigneConformite[] {
  return TYPES_CONFORMITE.flatMap((type): LigneConformite[] => {
    const duType = documents
      .filter((d) => d.type === type.id)
      .sort(
        (a, b) =>
          (b.expireLe ?? '9999').localeCompare(a.expireLe ?? '9999') ||
          b.ajouteLe.localeCompare(a.ajouteLe)
      );
    const [dernier] = duType;
    if (!dernier) {
      return type.obligatoire
        ? [
            {
              type: type.id,
              obligatoire: true,
              document: null,
              etat: { statut: 'manquant' as const },
            },
          ]
        : [];
    }
    return [
      {
        type: type.id,
        obligatoire: type.obligatoire,
        document: { id: dernier.id, expireLe: dernier.expireLe },
        etat: etatDocument(dernier.expireLe, jour),
      },
    ];
  });
}

/** L'état global d'un partenaire : le pire de ses documents obligatoires. */
export function etatGlobal(lignes: readonly LigneConformite[]): StatutConformite {
  return lignes
    .filter((l) => l.obligatoire)
    .reduce<StatutConformite>(
      (pire, l) => (GRAVITE[l.etat.statut] > GRAVITE[pire] ? l.etat.statut : pire),
      'a-jour'
    );
}

/** « Expire dans 11 jours », « Expiré depuis 3 jours », « Manquant », « À jour ». */
export function libelleEtat(etat: EtatConformite): string {
  switch (etat.statut) {
    case 'manquant':
      return 'Manquant';
    case 'expire': {
      const j = Math.abs(etat.jours ?? 0);
      return j === 0 ? 'Expiré' : `Expiré depuis ${j} jour${j > 1 ? 's' : ''}`;
    }
    case 'bientot': {
      const j = etat.jours ?? 0;
      return j === 0 ? 'Expire aujourd’hui' : `Expire dans ${j} jour${j > 1 ? 's' : ''}`;
    }
    case 'a-jour':
      return 'À jour';
  }
}

/** La date d'échéance proposée par défaut à l'ajout d'un document daté du jour. */
export function echeanceParDefaut(type: TypeConformite, jour: string): string | null {
  const mois = TYPES_CONFORMITE.find((t) => t.id === type)?.validiteMois ?? null;
  if (mois === null) return null;
  const [a, m, j] = jour.split('-').map(Number);
  const date = new Date(Date.UTC(a ?? 2000, (m ?? 1) - 1 + mois, j ?? 1));
  return date.toISOString().slice(0, 10);
}
